import { useState, useEffect } from 'react';
import { MessageSquare, Send, Loader2, User, CheckCircle2, AlertCircle, Clock, UserPlus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { projectId } from '../utils/supabase/info';
import { MentionTextarea, MentionDisplay } from './MentionTextarea';

interface Activity {
  id: string;
  type: 'assignment' | 'discovery_update' | 'comment' | 'submit_for_review' | 'request_changes' | 'approve';
  message: string;
  userId: string;
  timestamp: string;
  commentId?: string;
}

interface Comment {
  id: string;
  text: string;
  userId: string;
  userName: string;
  userRole: string;
  timestamp: string;
  isChangeRequest?: boolean;
  mentions?: string[];
}

interface ConfigItemActivityFeedProps {
  itemId: string;
  itemName?: string;
  companyId: string;
  accessToken: string;
  currentUser: any;
}

export function ConfigItemActivityFeed({
  itemId,
  itemName,
  companyId,
  accessToken,
  currentUser,
}: ConfigItemActivityFeedProps) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    console.log('[ConfigItemActivityFeed] Component mounted/updated with itemId:', itemId);
    if (!itemId || itemId === 'undefined') {
      console.error('[ConfigItemActivityFeed] ERROR: Invalid itemId received:', itemId);
    }
    loadActivityAndComments();
  }, [itemId]);

  const loadActivityAndComments = async () => {
    try {
      setLoading(true);

      // Load activity
      const activityResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${itemId}/activity`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (activityResponse.ok) {
        const activityData = await activityResponse.json();
        setActivities(activityData.activities || []);
      }

      // Load comments
      const commentsResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${itemId}/comments`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (commentsResponse.ok) {
        const commentsData = await commentsResponse.json();
        setComments(commentsData.comments || []);
      }
    } catch (error) {
      console.error('Error loading activity and comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostComment = async () => {
    if (!newComment.trim()) return;

    console.log('[ConfigItemActivityFeed] Posting comment with:', {
      companyId,
      itemId,
      itemName,
      userId: currentUser.id,
      userName: currentUser.name || currentUser.email,
      mentions: mentionedUsers
    });

    if (!itemId || itemId === 'undefined') {
      console.error('[ConfigItemActivityFeed] ERROR: itemId is invalid:', itemId);
      alert('Error: Cannot post comment - item ID is missing');
      return;
    }

    try {
      setPosting(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${itemId}/comments`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text: newComment,
            userId: currentUser.id,
            userName: currentUser.name || currentUser.email,
            userRole: currentUser.role || 'client',
            mentions: mentionedUsers,
            itemName: itemName || 'Config Item',
          }),
        }
      );

      if (response.ok) {
        setNewComment('');
        setMentionedUsers([]);
        loadActivityAndComments(); // Reload to show new comment
      }
    } catch (error) {
      console.error('Error posting comment:', error);
    } finally {
      setPosting(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'assignment':
        return <UserPlus className="size-4 text-blue-600" />;
      case 'submit_for_review':
        return <Send className="size-4 text-orange-600" />;
      case 'approve':
        return <CheckCircle2 className="size-4 text-green-600" />;
      case 'request_changes':
        return <AlertCircle className="size-4 text-orange-600" />;
      case 'comment':
        return <MessageSquare className="size-4 text-gray-600" />;
      default:
        return <Clock className="size-4 text-gray-400" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="size-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="size-5" />
          Activity & Comments
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Comment input */}
        <div className="space-y-2">
          <MentionTextarea
            value={newComment}
            onChange={(value, mentions) => {
              setNewComment(value);
              setMentionedUsers(mentions);
            }}
            placeholder="Add a comment or ask a question... Type @ to mention someone"
            companyId={companyId}
            accessToken={accessToken}
            rows={3}
          />
          <div className="flex justify-end">
            <Button
              onClick={handlePostComment}
              disabled={!newComment.trim() || posting}
              size="sm"
            >
              {posting ? (
                <>
                  <Loader2 className="size-4 mr-2 animate-spin" />
                  Posting...
                </>
              ) : (
                <>
                  <Send className="size-4 mr-2" />
                  Post Comment
                </>
              )}
            </Button>
          </div>
        </div>

        <Separator />

        {/* Activity and comments timeline */}
        <div className="space-y-4">
          {activities.length === 0 && comments.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No activity yet. Start by adding a comment or completing the discovery session.
            </p>
          ) : (
            <div className="space-y-3">
              {/* Merge and sort activities and comments by timestamp */}
              {[
                ...activities.map(a => ({ ...a, isActivity: true })),
                ...comments.map(c => ({ ...c, isActivity: false, message: c.text })),
              ]
                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                .map((item: any) => (
                  <div key={item.id} className="flex gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {item.isActivity ? (
                        getActivityIcon(item.type)
                      ) : (
                        <User className="size-4 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {item.isActivity ? item.message : item.userName}
                        </span>
                        {!item.isActivity && item.userRole === 'consultant' && (
                          <Badge variant="secondary" className="text-xs">
                            Consultant
                          </Badge>
                        )}
                        {item.isChangeRequest && (
                          <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                            Changes Requested
                          </Badge>
                        )}
                      </div>
                      {!item.isActivity && (
                        <p className="text-sm text-gray-700">
                          <MentionDisplay text={item.text} />
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {formatTimestamp(item.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
